import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getMultiFactorResolver, MultiFactorResolver, OAuthProvider, SAMLAuthProvider, signInWithEmailAndPassword, signInWithPopup, TotpMultiFactorGenerator } from 'firebase/auth';
import { httpsCallable } from '../lib/callable';
import { AlertCircle, Building2, CheckCircle2 } from 'lucide-react';
import { auth, functions } from '../firebase';
import { CLOUD_FUNCTIONS } from '../constants/firebase';
import { BrandLogo, useBranding } from '../contexts/BrandingContext';

function errorCode(err: unknown): string {
  return typeof err === 'object' && err !== null && 'code' in err
    ? String((err as { code: unknown }).code)
    : '';
}

function friendlyAuthError(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password. If you\'ve forgotten it, use "Forgot password?" below.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export default function Login() {
  const { branding } = useBranding();
  const [ssoProviders, setSsoProviders] = useState<Array<{providerId:string;displayName:string;protocol:'oidc'|'saml'}>>([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    httpsCallable(functions, CLOUD_FUNCTIONS.LIST_SSO_PROVIDERS)({})
      .then((result) => setSsoProviders(result.data as typeof ssoProviders))
      .catch(() => setSsoProviders([]));
  }, []);

  const beginMfa = (err: unknown) => {
    setMfaResolver(getMultiFactorResolver(auth, err as Parameters<typeof getMultiFactorResolver>[1]));
  };

  const handleEmailAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      const nextPath = searchParams.get('next');
      navigate(nextPath ? decodeURIComponent(nextPath) : '/');
    } catch (err: unknown) {
      if (errorCode(err) === 'auth/multi-factor-auth-required') {
        beginMfa(err);
      } else {
        setError(friendlyAuthError(errorCode(err)));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSso = async (provider: typeof ssoProviders[number]) => {
    setLoading(true); setError('');
    try {
      const authProvider = provider.protocol === 'saml' ? new SAMLAuthProvider(provider.providerId) : new OAuthProvider(provider.providerId);
      await signInWithPopup(auth, authProvider);
      navigate('/');
    } catch (err: unknown) {
      if (errorCode(err) === 'auth/multi-factor-auth-required') beginMfa(err);
      else if (errorCode(err) !== 'auth/popup-closed-by-user') setError('Single sign-on could not be completed. Contact your administrator.');
    } finally { setLoading(false); }
  };

  const handleMfa = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!mfaResolver) return;
    setLoading(true); setError('');
    try {
      const hint = mfaResolver.hints.find((item) => item.factorId === TotpMultiFactorGenerator.FACTOR_ID);
      if (!hint) throw new Error('No authenticator factor');
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaCode);
      await mfaResolver.resolveSignIn(assertion);
      navigate('/');
    } catch {
      setError('That authenticator code is invalid or expired. Try the latest code.');
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    setError('');
    setInfo('');
    if (!email) {
      setError('Enter your email above first, then click "Forgot password?".');
      return;
    }
    setLoading(true);
    try {
      const requestReset = httpsCallable(functions, CLOUD_FUNCTIONS.REQUEST_PASSWORD_RESET);
      await requestReset({ email, baseUrl: window.location.origin });
    } catch {
      // Do not reveal whether the account exists or whether delivery failed.
    }
    setInfo(
      `If an account exists for ${email}, a password reset link is on its way from ` +
      `no-reply@documentspark.com. Check your inbox (and your spam folder).`,
    );
    setLoading(false);
  };

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-[#F7F9FB] px-6 py-14 text-[#0B1C30]"
      style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        backgroundImage:
          'radial-gradient(circle at 88% 8%, rgba(0,137,137,.16), transparent 30%), radial-gradient(circle at 10% 92%, rgba(255,185,92,.12), transparent 26%)',
      }}
    >
      <section className="w-full max-w-[520px] rounded-[22px] border border-[#DCE2E5] bg-white px-8 py-10 shadow-[0_18px_48px_rgba(15,42,50,0.12)] sm:px-12 sm:py-12">
          <div className="mb-12 flex items-center gap-4">
            <BrandLogo className="h-[52px] w-[52px] rounded-[13px] object-contain" />
            <div>
              <div className="text-[21px] font-bold leading-tight text-[#001820]">{branding.productName}</div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#5F696C]">
                Study Build Automation
              </div>
            </div>
          </div>

          <div className="mb-8">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Client access</p>
            <h1 className="text-4xl font-normal tracking-[-0.04em] text-[#001820]">{mfaResolver ? 'Verify it’s you' : 'Welcome back'}</h1>
            <p className="mt-4 text-[15px] leading-6 text-[#5F696C]">{mfaResolver ? 'Enter the current code from your authenticator app.' : 'Sign in with your assigned account.'}</p>
          </div>

          {error && (
            <div className="mb-4 flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          {info && (
            <div className="mb-4 flex items-center space-x-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-800">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{info}</span>
            </div>
          )}

          {mfaResolver ? <form onSubmit={handleMfa} className="space-y-5">
            <label className="block text-sm font-bold text-[#0B1C30]">Authenticator code<input autoFocus required inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))} className="mt-2 h-14 w-full rounded-[13px] border border-[#CAD2D5] px-4 text-center text-xl tracking-[0.3em] outline-none focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/15" placeholder="000000" /></label>
            <button type="submit" disabled={loading || mfaCode.length !== 6} className="w-full rounded-full bg-[#001820] px-[22px] py-3.5 text-[15px] font-bold text-white disabled:opacity-50">{loading ? 'Verifying…' : 'Verify and sign in'}</button>
            <button type="button" onClick={() => { setMfaResolver(null); setMfaCode(''); setError(''); }} className="w-full text-sm font-semibold text-[var(--brand-primary)]">Back to sign in</button>
          </form> : <form onSubmit={handleEmailAuth} className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-bold text-[#0B1C30]" htmlFor="email">Email address</label>
              <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="h-14 w-full rounded-[13px] border border-[#CAD2D5] px-4 text-base outline-none transition placeholder:text-[#8A9599] focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/15"
                  placeholder="name@example.com"
                  autoComplete="email"
                  required
                />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-sm font-bold text-[#0B1C30]" htmlFor="password">Password</label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="text-sm font-semibold text-[var(--brand-primary)] transition-colors hover:text-[#004F51] disabled:opacity-50"
                >
                  Forgot password?
                </button>
              </div>
              <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="h-14 w-full rounded-[13px] border border-[#CAD2D5] px-4 text-base outline-none transition focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/15"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  minLength={6}
                />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#001820] px-[22px] py-3.5 text-[15px] font-bold text-white shadow-[0_8px_20px_rgba(0,24,32,0.16)] transition hover:bg-[#00313A] focus:ring-4 focus:ring-[var(--brand-primary)]/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Please wait...' : 'Sign in'}
            </button>
          </form>}

          {!mfaResolver && ssoProviders.length > 0 && <div className="mt-7">
            <div className="mb-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-[#7B898D]"><span className="h-px flex-1 bg-[#DCE2E5]" />or use company SSO<span className="h-px flex-1 bg-[#DCE2E5]" /></div>
            <div className="space-y-3">{ssoProviders.map((provider) => <button key={provider.providerId} type="button" disabled={loading} onClick={() => handleSso(provider)} className="flex w-full items-center justify-center gap-2 rounded-full border border-[#CAD2D5] bg-white px-5 py-3.5 text-sm font-bold text-[#274249] transition hover:border-[var(--brand-primary)] hover:bg-[#F7FBFA] disabled:opacity-50"><Building2 className="h-4 w-4 text-[var(--brand-primary)]" />Continue with {provider.displayName}</button>)}</div>
          </div>}

          <p className="mt-12 border-t border-[#E3E8EA] pt-7 text-center text-sm leading-5 text-[#5F696C]">
            Need access? Contact your organization’s DocumentSpark administrator.
          </p>
      </section>
    </main>
  );
}
